<template>
  <nav
    v-show="isVisible"
    id="navigation"
    class="main-navigation"
    :aria-expanded="isVisible"
    aria-label="Main Navigation"
    :class="{ '-visible': isVisible, '-open': isOpen }"
  >
    <ul class="navigation -main" v-if="mainNavigation">
      <li
        class="item"
        v-for="(navItem, index) in mainNavigation.navLinks"
        :key="`navitem-${index}`"
      >
        <NuxtLink
          v-if="navItem.link?.slug"
          class="link nav-link-default"
          :to="getPageLink(navItem.link)"
        >
          {{ navItem.label }}
        </NuxtLink>
      </li>
    </ul>
    <ul class="navigation -services">
      <p class="title">Bereiche</p>
      <li
        class="item"
        v-for="(service, index) in allServices"
        :key="`service-${index}`"
      >
        <NuxtLink
          v-if="service"
          class="link nav-link-default"
          :to="`/services/${service.slug}`"
        >
          {{ service.title }}
        </NuxtLink>
      </li>
    </ul>
    <ul class="links" v-if="headerContent">
      <li
        class="item"
        v-for="(link, index) in filteredContactLinks"
        :key="`contact-link-${index}`"
      >
        <DatoLink class="link nav-link-default" :data="link" />
      </li>
    </ul>
    <DatoAsset
      :lazy-load="true"
      class="asset"
      v-if="headerContent"
      :asset="headerContent && headerContent.navigationAsset"
      :data="headerContent.navigationAsset"
    />
  </nav>
</template>

<script setup lang="ts">
import { useScrollLock } from '@vueuse/core';
import gsap from 'gsap';
import type {
  GetHeaderQuery,
  GetMainNavigationQuery,
  GetServicesQuery
} from '#gql';
import DatoLink from '~/dato/DatoLink.vue';
import DatoAsset from '~/dato/DatoAsset.vue';
import {
  resolveInternalUrl,
  type InternalUrlRecord
} from '~/utils/pageRouting';

const bodyElement = ref<HTMLElement | null>(null);
const isLocked = useScrollLock(bodyElement);

const tl = gsap.timeline({
  paused: true
});

const animationDuration = 0.6;

const props = defineProps<{
  isVisible: boolean;
  isOpen: boolean;
}>();

const { data: dataHeader } = await useAsyncGql({
  operation: 'GetHeader'
});

const { data: dataMainNavigation } = await useAsyncGql({
  operation: 'GetMainNavigation'
});

const { data: dataServices } = await useAsyncGql({
  operation: 'GetServices'
});

const headerContent = (dataHeader.value as GetHeaderQuery).header;
const mainNavigation = (dataMainNavigation.value as GetMainNavigationQuery)
  .mainNavigation;
const allServices = (dataServices.value as GetServicesQuery).allServices;
type ContactLink = NonNullable<NonNullable<GetHeaderQuery['header']>['contactLinks']>[number];

function hasAnyContactUrl(link: ContactLink) {
  const hasEmailUrl = Boolean(link.emailUrl?.trim());
  const hasExternalUrl = Boolean(link.externalUrl?.trim());
  const hasInternalUrl = Boolean(link.internalUrl?.slug?.trim());
  return hasEmailUrl || hasExternalUrl || hasInternalUrl;
}

const filteredContactLinks = computed(() => {
  const links = (headerContent?.contactLinks ?? []) as ContactLink[];
  return links.filter((link) => hasAnyContactUrl(link));
});

watchEffect(function () {
  if (props.isOpen) {
    isLocked.value = true;
    tl.play(0);
  } else {
    isLocked.value = false;
    tl.reverse();
  }
});

onMounted(function () {
  bodyElement.value = document.body;

  tl.fromTo(
    '.main-navigation > .navigation',
    {
      y: '100%',
      opacity: 0
    },
    {
      y: 0,
      opacity: 1,
      ease: 'power2.out',
      duration: animationDuration
    }
  )
    .fromTo(
      '.main-navigation > .links',
      {
        y: '100%',
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        ease: 'power2.out',
        duration: animationDuration
      },
      '<'
    )
    .fromTo(
      '.main-navigation > .asset',
      {
        y: '-100%',
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        ease: 'power2.out',
        duration: animationDuration
      },
      '<'
    );
});

// Nav entries can point at pages, services or projects, and each type sits
// under its own route prefix. resolveInternalUrl owns that mapping; building
// the path from the page parent chain alone dropped the prefix and produced
// dead links like /holzbau instead of /services/holzbau.
function getPageLink(link: InternalUrlRecord) {
  return resolveInternalUrl(link);
}
</script>

<style scoped lang="scss">
.main-navigation {
  position: fixed;
  inset: 0;
  display: grid;
  z-index: 20;
  padding: 0 var(--base-component-padding-inline) 2rem
    var(--base-component-padding-inline);
  padding-top: 4.75rem;
  background-color: var(--color-off-white);
  color: var(--color-black);
  overflow-y: auto;

  @media (--vs) {
    grid-template-rows: auto 1fr auto;
    gap: 2.5rem;
  }

  @media (--vl) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    // The asset row is the flexible one: Bereiche and the contact links always
    // get their natural height, and the asset absorbs whatever is left. On a
    // wide/flat window that shrinks the video instead of pushing them off-screen.
    grid-template-rows: minmax(0, 1fr) auto auto;
  }

  &[aria-expanded='true'] {
    @media (--vl) {
      gap: 3.5rem 1rem;
    }
  }

  &[aria-expanded='false'] {
    pointer-events: none;
  }

  > .navigation.-main {
    grid-area: 1 / 1 / 2 / 2;

    @media (--vs) {
      align-self: center;
      padding-top: 4rem;
    }

    @media (--vl) {
      grid-area: 1 / 1 / 3 / 2;
      align-self: center;
    }

    > .item {
      > .link {
        text-decoration: none;
        display: inline-block;
        line-height: 1 !important;

        @media (--vs) {
          @include text-2xl(700);

          margin: 0.75rem 0;
        }

        @media (--vl) {
          @include text-2xl(700);

          margin: 0.75rem 0;
        }
      }
    }
  }

  > .navigation.-services {
    grid-area: 2 / 1 / 3 / 2;

    @media (--vl) {
      grid-area: 2 / 2 / 3 / 3;
      align-self: flex-start;
    }

    > .title {
      color: var(--color-grey);
      padding-bottom: 0.5rem;

      @media (--vs) {
        @include text-lg(700);
      }

      @media (--vl) {
        @include text-md(700);
      }
    }

    > .item {
      > .link {
        text-decoration: none;
        display: inline-block;
        line-height: 1 !important;

        @media (--vs) {
          @include text-2xl(700);

          font-size: 2rem !important;
          line-height: 1.5 !important;
          margin: 0.3rem 0;
        }

        @media (--vl) {
          @include text-xl(700);
          margin: 0.6rem 0;
        }
      }
    }
  }

  > .links {
    display: flex;
    gap: 2rem;
    grid-area: 3 / 1 / 4 / 2;
    align-self: end;

    > .item:not(:last-of-type) {
      padding-bottom: 0.5rem;
    }

    @media (--vs) {
      display: none;
    }
  }

  > .asset {
    @media (--vs) {
      display: none;
    }

    @media (--vl) {
      grid-area: 1 / 2 / 2 / 3;
      align-self: stretch;
      min-height: 0;
      max-height: 45svh;
      overflow: hidden;
    }

    > .image {
      height: 100%;
    }
  }
}
</style>
